import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Trash2, Trophy, X, MinusCircle, RotateCcw, Send, Copy, Gift, Pencil } from "lucide-react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { todayUKDate } from "@/lib/device";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("dr_admin_token") || "");
  const [password, setPassword] = useState("");
  const [tips, setTips] = useState([]);
  const [stats, setStats] = useState(null);
  const [freeMode, setFreeMode] = useState(false);
  const [extraInfo, setExtraInfo] = useState("");
  const [extraInfoSaving, setExtraInfoSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    race_date: todayUKDate(),
    meeting: "",
    race_time: "",
    selection: "",
    odds: "",
    stake: "1pt Win",
    rationale: "",
  });

  const loadAll = async (t = token) => {
    if (!t) return;
    try {
      const [tipsRes, statsRes, cfgRes, extraRes] = await Promise.all([
        adminApi.listTips(t),
        adminApi.stats(t),
        adminApi.getConfig(t),
        adminApi.getExtraInfo(),
      ]);
      setTips(tipsRes.tips || []);
      setStats(statsRes);
      setFreeMode(!!cfgRes.free_mode);
      setExtraInfo(extraRes?.text || "");
    } catch (e) {
      toast.error("Session expired");
      setToken("");
      localStorage.removeItem("dr_admin_token");
    }
  };

  const toggleFreeMode = async () => {
    const next = !freeMode;
    if (
      next &&
      !window.confirm("Turn ON Free Mode? Every tip will be visible to everyone without payment.")
    )
      return;
    if (!next && !window.confirm("Turn OFF Free Mode? Punters will have to pay £2 again."))
      return;
    try {
      const r = await adminApi.setFreeMode(token, next);
      setFreeMode(!!r.free_mode);
      toast.success(next ? "Free Mode ON — tips are open" : "Free Mode OFF — £2 unlock resumed");
    } catch {
      toast.error("Failed to update");
    }
  };

  // If called with a string arg, save that value (used by Clear button).
  // Otherwise save the current `extraInfo` state.
  const saveExtraInfo = async (overrideValue) => {
    const value = typeof overrideValue === "string" ? overrideValue : extraInfo;
    setExtraInfoSaving(true);
    try {
      const r = await adminApi.setExtraInfo(token, value);
      setExtraInfo(r?.text || "");
      toast.success(value ? "Extra info saved" : "Extra info cleared");
    } catch {
      toast.error("Failed to save extra info");
    } finally {
      setExtraInfoSaving(false);
    }
  };

  useEffect(() => {
    if (token) loadAll(token);
  }, []);

  const login = async () => {
    try {
      const res = await adminApi.login(password);
      setToken(res.token);
      localStorage.setItem("dr_admin_token", res.token);
      await loadAll(res.token);
      toast.success("Logged in");
    } catch (e) {
      toast.error("Wrong password");
    }
  };

  const createTip = async (e) => {
    e.preventDefault();
    if (editingId) {
      try {
        await adminApi.editTip(token, editingId, form);
        toast.success("Tip updated");
        setEditingId(null);
        setForm({ ...form, meeting: "", race_time: "", selection: "", odds: "", rationale: "" });
        await loadAll();
      } catch {
        toast.error("Update failed");
      }
      return;
    }
    // New tip path: create tip first, then bundle-save extra info independently
    // so a failure on one doesn't mislead the admin about the other.
    let tipOk = false;
    try {
      await adminApi.createTip(token, form);
      tipOk = true;
    } catch {
      toast.error("Failed to post tip");
      return;
    }
    try {
      await adminApi.setExtraInfo(token, extraInfo || "");
      toast.success(
        extraInfo?.trim()
          ? "Tip posted · Extra info saved"
          : "Tip posted"
      );
    } catch {
      toast.error("Tip posted, but extra info failed to save. Try Save again.");
    }
    setForm({ ...form, meeting: "", race_time: "", selection: "", odds: "", rationale: "" });
    await loadAll();
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({
      race_date: t.race_date,
      meeting: t.meeting || "",
      race_time: t.race_time || "",
      selection: t.selection || "",
      odds: t.odds || "",
      stake: t.stake || "1pt Win",
      rationale: t.rationale || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({
      race_date: todayUKDate(),
      meeting: "",
      race_time: "",
      selection: "",
      odds: "",
      stake: "1pt Win",
      rationale: "",
    });
  };

  const setResult = async (id, result) => {
    try {
      const pts = result === "won" ? Number(prompt("Return in points (e.g. 3.5)?", "1")) || 1 : result === "lost" ? -1 : 0;
      await adminApi.updateResult(token, id, { result, return_pts: pts });
      await loadAll();
      toast.success(`Marked ${result}`);
    } catch {
      toast.error("Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this tip?")) return;
    await adminApi.deleteTip(token, id);
    await loadAll();
  };

  const duplicateToTomorrow = async (t) => {
    const d = new Date(t.race_date + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + 1);
    const tomorrow = d.toISOString().slice(0, 10);
    try {
      await adminApi.createTip(token, {
        race_date: tomorrow,
        meeting: t.meeting,
        race_time: t.race_time,
        selection: t.selection,
        odds: t.odds || "",
        stake: t.stake || "1pt Win",
        rationale: t.rationale || "",
      });
      toast.success(`Duplicated to ${tomorrow}`);
      await loadAll();
    } catch {
      toast.error("Duplicate failed");
    }
  };

  const refund = async (id) => {
    if (!window.confirm("Refund all paid unlocks for this tip's day?")) return;
    try {
      const r = await adminApi.refundDay(token, id);
      toast.success(`Refunded ${r.refunded}`);
      await loadAll();
    } catch {
      toast.error("Refund failed");
    }
  };

  const broadcast = async () => {
    const body = prompt(
      "Push body?",
      "Tap to reveal today's DailyRacer card."
    );
    if (body === null) return;
    try {
      const r = await adminApi.broadcast(token, {
        title: "DR — Today's tip is live 🐎",
        body,
        url: "/",
      });
      const errSummary = r.errors && Object.keys(r.errors).length
        ? " · errors: " + Object.entries(r.errors).map(([k, v]) => `${k}×${v}`).join(", ")
        : "";
      if (r.sent > 0 && r.failed === 0) {
        toast.success(`Sent ${r.sent} · pruned ${r.pruned}`);
      } else {
        toast.error(`Sent ${r.sent} · ${r.failed} failed · pruned ${r.pruned}${errSummary}`);
        if (r.samples && r.samples.length) {
          console.warn("Push failure samples:", r.samples);
        }
      }
    } catch {
      toast.error("Broadcast failed");
    }
  };

  if (!token) {
    return (
      <div className="dr-shell flex min-h-screen items-center justify-center px-6">
        <div className="w-full">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Admin</div>
          <h1 className="mt-2 font-display text-4xl text-white">Sign in</h1>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="mt-6 w-full rounded-full border border-zinc-800 bg-black px-4 py-3 text-white"
          />
          <button
            data-testid="admin-login-btn"
            onClick={login}
            className="dr-btn-primary mt-3 w-full rounded-full px-6 py-3"
          >
            Enter
          </button>
          <Link to="/" className="mt-6 inline-block text-xs uppercase tracking-widest text-zinc-500">
            ← Back
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="dr-shell pb-24">
      <header className="dr-glass sticky top-0 z-20 flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-zinc-400 hover:text-white">
            <ChevronLeft />
          </Link>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">DailyRacer</div>
            <div className="font-display text-lg leading-none text-white">Admin</div>
          </div>
        </div>
        <button
          onClick={() => {
            setToken("");
            localStorage.removeItem("dr_admin_token");
          }}
          data-testid="admin-logout-btn"
          className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          Sign out
        </button>
      </header>

      {stats && (
        <section className="grid grid-cols-4 gap-2 px-6 pt-6">
          <MiniStat label="Tips" value={stats.total_tips} />
          <MiniStat label="Unlocks" value={stats.total_unlocks} />
          <MiniStat label="Push" value={stats.push_subscribers ?? 0} />
          <MiniStat label="Revenue" value={`£${stats.revenue_gbp}`} />
        </section>
      )}

      <section className="mt-4 px-6 space-y-2">
        <button
          onClick={toggleFreeMode}
          data-testid="free-mode-toggle"
          className={`flex w-full items-center justify-between rounded-full border px-4 py-3 text-sm font-bold uppercase tracking-widest ${
            freeMode
              ? "border-[#E1FF00]/60 bg-[#E1FF00]/15 text-[#E1FF00]"
              : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
          }`}
        >
          <span className="flex items-center gap-2">
            <Gift size={14} /> Free Mode {freeMode ? "· ON" : "· OFF"}
          </span>
          <span className="text-[10px] font-mono-data font-normal opacity-70">
            {freeMode ? "Tap to end" : "Tap to open all tips"}
          </span>
        </button>

        <button
          onClick={broadcast}
          data-testid="admin-broadcast-btn"
          className="flex w-full items-center justify-center gap-2 rounded-full border border-[#E1FF00]/40 bg-[#E1FF00]/10 px-4 py-3 text-sm font-bold uppercase tracking-widest text-[#E1FF00]"
        >
          <Send size={14} /> Send today's tip alert
        </button>
      </section>

      <section className="mt-4 px-6" data-testid="admin-extra-info-section">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#E1FF00]">
              Today's Extra Information
            </div>
            {extraInfoSaving && (
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                Saving…
              </span>
            )}
          </div>
          <textarea
            data-testid="admin-extra-info-textarea"
            rows={3}
            value={extraInfo}
            onChange={(e) => setExtraInfo(e.target.value)}
            placeholder="Type extra info here BEFORE hitting Post Today's Card — it saves in the same click. Or edit anytime and tap Save. Empty to hide the box."
            className="w-full resize-none rounded-lg border border-zinc-800 bg-black/40 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:border-[#E1FF00]/50 focus:outline-none"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={saveExtraInfo}
              disabled={extraInfoSaving}
              data-testid="admin-extra-info-save"
              className="rounded-full border border-[#E1FF00]/60 bg-[#E1FF00]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#E1FF00] disabled:opacity-50"
            >
              Save
            </button>
            {extraInfo && (
              <button
                onClick={() => {
                  setExtraInfo("");
                  saveExtraInfo("");
                }}
                data-testid="admin-extra-info-clear"
                className="rounded-full border border-zinc-800 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-zinc-400 hover:bg-zinc-800"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="mt-6 px-6">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {editingId ? "Edit tip" : "Post tip"}
            </div>
            {editingId && (
              <button
                type="button"
                onClick={cancelEdit}
                data-testid="tip-cancel-edit-btn"
                className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white"
              >
                Cancel
              </button>
            )}
          </div>
          <form onSubmit={createTip} className="grid grid-cols-2 gap-3">
            <Input label="Date" type="date" value={form.race_date} onChange={(v) => setForm({ ...form, race_date: v })} testid="tip-date" />
            <Input label="Off time" placeholder="15:35" value={form.race_time} onChange={(v) => setForm({ ...form, race_time: v })} testid="tip-time" />
            <Input label="Meeting" placeholder="Ascot" value={form.meeting} onChange={(v) => setForm({ ...form, meeting: v })} testid="tip-meeting" />
            <Input label="Selection" placeholder="Frankel" value={form.selection} onChange={(v) => setForm({ ...form, selection: v })} testid="tip-selection" />
            <Input label="Odds" placeholder="5/2" value={form.odds} onChange={(v) => setForm({ ...form, odds: v })} testid="tip-odds" />
            <Input label="Stake" placeholder="1pt Win" value={form.stake} onChange={(v) => setForm({ ...form, stake: v })} testid="tip-stake" />
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Rationale</label>
              <textarea
                value={form.rationale}
                onChange={(e) => setForm({ ...form, rationale: e.target.value })}
                rows={3}
                data-testid="tip-rationale"
                className="mt-1 w-full rounded-xl border border-zinc-800 bg-black px-3 py-2 text-sm text-white"
              />
            </div>
            <button
              type="submit"
              data-testid="tip-create-btn"
              className="dr-btn-primary col-span-2 rounded-full px-6 py-3"
            >
              {editingId ? "Save changes" : "Post to today's card"}
            </button>
          </form>
        </div>
      </section>

      <section className="mt-8 px-6">
        <div className="mb-3 text-[10px] uppercase tracking-[0.24em] text-zinc-500">Recent tips</div>
        <div className="space-y-3">
          {tips.map((t) => (
            <div key={t.id} className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4" data-testid={`admin-tip-${t.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono-data text-xs text-zinc-500">{t.race_date} · {t.race_time}</div>
                  <div className="font-display text-lg text-white">{t.selection}</div>
                  <div className="text-xs text-zinc-500">{t.meeting} · {t.odds} · {t.stake}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">{t.result}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <IconBtn onClick={() => startEdit(t)} icon={Pencil} label="Edit" testid={`edit-${t.id}`} />
                <IconBtn onClick={() => setResult(t.id, "won")} icon={Trophy} label="Won" testid={`won-${t.id}`} />
                <IconBtn onClick={() => setResult(t.id, "lost")} icon={X} label="Lost" testid={`lost-${t.id}`} />
                <IconBtn onClick={() => setResult(t.id, "void")} icon={MinusCircle} label="Void" testid={`void-${t.id}`} />
                <IconBtn onClick={() => duplicateToTomorrow(t)} icon={Copy} label="Tomorrow" testid={`dup-${t.id}`} accent />
                <IconBtn onClick={() => refund(t.id)} icon={RotateCcw} label="Refund day" testid={`refund-${t.id}`} />
                <IconBtn onClick={() => del(t.id)} icon={Trash2} label="Delete" testid={`delete-${t.id}`} danger />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1 w-full rounded-xl border border-zinc-800 bg-black px-3 py-2 text-sm text-white"
      />
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-3">
      <div className="text-[9px] uppercase tracking-[0.24em] text-zinc-500">{label}</div>
      <div className="mt-1 font-display text-xl text-white">{value}</div>
    </div>
  );
}

function IconBtn({ onClick, icon: Icon, label, danger, accent, testid }) {
  const cls = danger
    ? "border-[#F87171]/40 text-[#F87171] hover:bg-[#F87171]/10"
    : accent
    ? "border-[#E1FF00]/40 text-[#E1FF00] hover:bg-[#E1FF00]/10"
    : "border-zinc-800 text-zinc-300 hover:bg-zinc-800";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest ${cls}`}
    >
      <Icon size={12} /> {label}
    </button>
  );
}
